import type { ComponentProps } from 'react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

/**
 * Shared props for the TechDocs Mermaid addon.
 *
 * The addon has to be registered in two independent places — the standalone reader route in
 * `App.tsx` and the entity Docs tab in `components/catalog/EntityPage.tsx` — and they previously
 * drifted, which is why diagrams rendered on one and not the other. Both now spread this object,
 * so they cannot diverge again.
 *
 * Why there is no `config` key: the addon already honours Backstage's active theme. Its
 * `selectConfig` picks `lightConfig` when the palette is light and `{ theme: 'dark', ...darkConfig }`
 * otherwise, and re-runs when the palette changes. But a `config` value is deep-merged *over* that
 * choice and wins, so setting `config.theme` forces one mermaid theme into both palettes — which is
 * how a light theme with black edges ended up rendering on the dark UI. Keep theme-specific values
 * in `lightConfig` / `darkConfig` only.
 */
export const mermaidAddonProps: ComponentProps<typeof Mermaid> = {
  // Pan/zoom is opt-in; without this large diagrams are unreadable and cannot be inspected.
  enableZoom: true,
  zoomOptions: {
    scaleExtent: [0.4, 10],
  },
  lightConfig: {
    theme: 'forest',
    themeVariables: { lineColor: '#000000' },
    // useMaxWidth:false stops mermaid shrinking the SVG to the content column width.
    flowchart: { useMaxWidth: false, curve: 'basis' },
  },
  darkConfig: {
    // Deliberately no `theme` — the addon defaults it to mermaid's built-in 'dark',
    // which supplies readable edge and label colours for the dark UI.
    flowchart: { useMaxWidth: false, curve: 'basis' },
  },
};
